import { useCallback, useEffect, useRef, useState } from "react";
import { useTenant } from "@/context/TenantContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AI_TYPES } from "@/components/documents/constants";
import { DocumentsHeader } from "@/components/documents/DocumentsHeader";
import { DocumentRequestsCard } from "@/components/documents/DocumentRequestsCard";
import { EvidenceCoverageCard } from "@/components/documents/EvidenceCoverageCard";
import { DocumentSuggestions, DocumentFilters } from "@/components/documents/DocumentSuggestions";
import { DocumentsList } from "@/components/documents/DocumentsList";
import { DuplicateDialog } from "@/components/documents/DuplicateDialog";
import { DocumentDetailDialog } from "@/components/documents/DocumentDetailDialog";

export default function Documents() {
  const { companyId, period, can } = useTenant();
  const isConsultant = can("analysis:notes:view");
  const canWrite = can("esg:write");
  const [docs, setDocs] = useState(null);
  const [requests, setRequests] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duplicate, setDuplicate] = useState(null); // {file, existing}
  const [detail, setDetail] = useState(null); // {document, fields, ...}
  const [detailLoading, setDetailLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [acting, setActing] = useState(false);
  const [editField, setEditField] = useState(null); // {id, value}
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      if (filterCategory) params.set("category", filterCategory);
      if (filterStatus) params.set("status", filterStatus);
      const { data } = await api.get(
        `/companies/${companyId}/periods/${period.id}/documents?${params}`);
      setDocs(data);
      return data;
    } catch {
      setDocs([]);
      return [];
    }
  }, [companyId, period?.id, search, filterCategory, filterStatus]);

  const loadAux = useCallback(async () => {
    if (!companyId || !period?.id) return;
    api.get(`/companies/${companyId}/periods/${period.id}/document-requests`)
      .then((r) => setRequests(r.data)).catch(() => setRequests([]));
    api.get(`/companies/${companyId}/periods/${period.id}/document-checklist`)
      .then((r) => setChecklist(r.data.items || [])).catch(() => setChecklist([]));
    api.get(`/companies/${companyId}/periods/${period.id}/evidence-coverage`)
      .then((r) => setCoverage(r.data)).catch(() => setCoverage(null));
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAux(); }, [loadAux]);

  // Polling mentre ci sono documenti in analisi (punto 181: "Stiamo analizzando…").
  useEffect(() => {
    const processing = (docs || []).some((d) => d.document_status === "PROCESSING");
    if (processing && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const fresh = await load();
        if (!fresh.some((d) => d.document_status === "PROCESSING")) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          loadAux();
        }
      }, 4000);
    }
    return () => {
      if (pollRef.current && !(docs || []).some((d) => d.document_status === "PROCESSING")) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [docs, load, loadAux]);

  const doUpload = async (file, strategy) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("auto_process", "true");
    if (strategy) fd.append("duplicate_strategy", strategy);
    return api.post(`/companies/${companyId}/periods/${period.id}/documents`, fd, {
      onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
    });
  };

  const uploadMany = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    setProgress(0);
    let ok = 0, dup = 0, err = 0;
    try {
      for (const file of files) {
        try {
          await doUpload(file);
          ok += 1;
        } catch (e) {
          if (e?.response?.status === 409 && e?.response?.data?.duplicate) {
            dup += 1;
            setDuplicate({ file, existing: e.response.data.existing });
            break; // una decisione alla volta
          }
          err += 1;
          toast.error(`${file.name}: ${formatApiError(e)}`);
        }
      }
      if (ok) toast.success(ok === 1
        ? "Documento caricato. Stiamo analizzando il documento…"
        : `${ok} documenti caricati. Analisi in corso…`);
      if (dup) toast.info("Un documento sembra già presente: scegli come procedere.");
      if (!ok && !dup && err) toast.error("Caricamento non riuscito");
      load();
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const resolveDuplicate = async (strategy) => {
    const d = duplicate;
    setDuplicate(null);
    if (strategy === "cancel" || !d) return;
    setUploading(true);
    try {
      const { data } = await doUpload(d.file, strategy);
      toast.success(strategy === "new_version"
        ? `Nuova versione (v${data.version}) caricata: la precedente è stata archiviata come sostituita.`
        : "Documento collegato comunque al Cassetto ESG.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const download = async (doc) => {
    try {
      const r = await api.get(
        `/companies/${companyId}/periods/${period.id}/documents/${doc.id}/download`,
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Eliminare "${doc.name}"? Se è un'evidenza di dati approvati verrà archiviato, non cancellato.`)) return;
    try {
      const { data } = await api.delete(
        `/companies/${companyId}/periods/${period.id}/documents/${doc.id}`);
      toast.success(data.archived ? "Documento archiviato (evidenza protetta)" : "Documento eliminato");
      if (detail?.document?.id === doc.id) closeDetail();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openDetail = async (doc) => {
    setDetailLoading(true);
    setDetail({ document: doc, fields: [] });
    try {
      const { data } = await api.get(
        `/companies/${companyId}/periods/${period.id}/documents/${doc.id}/detail`);
      setDetail(data);
      if (AI_TYPES.includes(doc.content_type || doc.mime_type)) {
        const r = await api.get(
          `/companies/${companyId}/periods/${period.id}/documents/${doc.id}/preview`,
          { responseType: "blob" });
        setPreviewUrl(URL.createObjectURL(r.data));
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetail(null);
    setEditField(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const fieldAction = async (field, action, value) => {
    setActing(true);
    try {
      await api.post(
        `/companies/${companyId}/periods/${period.id}/fields/${field.id}/${action}`,
        action === "correct" ? { value } : undefined);
      toast.success(action === "accept" ? "Dato confermato e salvato come bozza"
        : action === "correct" ? "Dato corretto e salvato come bozza"
        : "Dato segnato come non corretto");
      setEditField(null);
      const { data } = await api.get(
        `/companies/${companyId}/periods/${period.id}/documents/${detail.document.id}/detail`);
      setDetail((prev) => ({ ...prev, ...data }));
      loadAux();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(false);
    }
  };

  const openRequests = requests.filter((r) => r.status === "OPEN");

  return (
    <div className="space-y-6" data-testid="documents-page">
      <DocumentsHeader
        canWrite={canWrite} uploading={uploading} progress={progress}
        fileRef={fileRef} onUploadMany={uploadMany}
      />

      {/* Documenti richiesti dal consulente (punto 64) */}
      <DocumentRequestsCard openRequests={openRequests} canWrite={canWrite} fileRef={fileRef} />

      {/* Copertura documentale (punto 122) */}
      <EvidenceCoverageCard coverage={coverage} isConsultant={isConsultant} />

      {/* Suggerimenti contestuali (punto 67) */}
      <DocumentSuggestions checklist={checklist} docs={docs} fileRef={fileRef} />

      {/* Ricerca e filtri (punti 90-91) */}
      <DocumentFilters
        docs={docs} search={search} onSearchChange={setSearch}
        filterCategory={filterCategory} onFilterCategoryChange={setFilterCategory}
        filterStatus={filterStatus} onFilterStatusChange={setFilterStatus}
      />

      {/* Lista documenti */}
      <DocumentsList
        docs={docs} canWrite={canWrite}
        onOpenDetail={openDetail} onDownload={download} onRemove={remove}
      />

      {/* Dialog duplicato (punto 10) */}
      <DuplicateDialog duplicate={duplicate} onResolve={resolveDuplicate} />

      {/* Dettaglio documento + review campi (punti 33, 92-94) */}
      <DocumentDetailDialog
        detail={detail} detailLoading={detailLoading} previewUrl={previewUrl}
        isConsultant={isConsultant} canWrite={canWrite} acting={acting}
        editField={editField} onEditFieldChange={setEditField}
        onFieldAction={fieldAction} onClose={closeDetail}
      />
    </div>
  );
}
